import { connect } from "react-redux";
import EgresosActions from "../../../stores/hospitalizacion/egresos/Actions";
import moment from "moment";
import { DATE_FORMATS } from "../../../utils/Constant";
import useEmpresa from "../../../hooks/useEmpresa";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomAlert from "../../../components/Common/CustomAlert";
import CheckRender from "../../../components/Common/CheckRender";
import { Section } from "../../../components/CustomLayout/CustomLayoutStyles";
import Tooltip from "../../../components/Common/Tooltip";
import { EyeOutlined, DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import Space from "../../../components/Common/Space";
import { useState, useMemo } from "react";
import { useUpdateEffect } from "../../../hooks/LifeCycle";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

const CompactSection = styled(Section)`
  padding: 8px 0;
  margin: 0;
`;

const Button = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${props => props.disabled ? '#ccc' : '#1890ff'};
  color: white;
  border-color: ${props => props.disabled ? '#ccc' : '#1890ff'};
  
  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : '#40a9ff'};
    color: white;
  }
`;

const BusquedaCierresCuentasTabla = (props) => {
  const { formConsultaCierresCuentasData, authUserData, consultarCierresCuentas } = props;
  const empresa = useEmpresa();
  const history = useHistory();

  const [dataFiltrada, setDataFiltrada] = useState([]);
  
  // Estados para el modal de voucher
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherRecord, setVoucherRecord] = useState(null);
  const [esMatricial, setEsMatricial] = useState(false);
  const [descargandoVoucher, setDescargandoVoucher] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});

  // Usar useMemo para evitar crear un nuevo array en cada render
  const listaOrigen = useMemo(
    () => formConsultaCierresCuentasData?.dataCierres?.cierreCuentas || [],
    [formConsultaCierresCuentasData?.dataCierres?.cierreCuentas]
  );

  // Obtener datos de paginación
  const cantidadTotal = formConsultaCierresCuentasData?.dataCierres?.cantidadRegistros || 0;
  const paginaActual = formConsultaCierresCuentasData?.paginaActual || 1;
  const tamanoPagina = formConsultaCierresCuentasData?.tamanoPagina || 10;
  const totalPaginas = Math.ceil(cantidadTotal / tamanoPagina);

  /* =========================================================
     FUNCIÓN: Normalizar texto (quitar espacios extras, trim, lowercase)
     ========================================================= */
  const normalizeText = (text) => {
    return String(text || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  };

  /* =========================================================
     EFECTO: FILTRADO LOCAL (SOLO NOMBRE ASEGURADO)
     ========================================================= */
  useUpdateEffect(() => {
    let data = [...listaOrigen];

    const {
      nombreAsegurado,
      cedula,
    } = formConsultaCierresCuentasData || {};

    // Filtro de Nombre Asegurado (en memoria)
    if (nombreAsegurado) {
      data = data.filter((x) => {
        const nombre = normalizeText(x.nombreAfiliado);
        const filtro = normalizeText(nombreAsegurado);
        return nombre.includes(filtro);
      });
    }

    // Filtro de Cédula (en memoria)
    if (cedula) {
      data = data.filter((x) =>
        String(x.cedula ?? "").includes(String(cedula))
      );
    }

    // Plan no se filtra en memoria; el backend ya filtra por Plan en la consulta

    setDataFiltrada(data);
  }, [
    listaOrigen,
    formConsultaCierresCuentasData?.nombreAsegurado,
    formConsultaCierresCuentasData?.cedula,
  ]);

  /* =========================================================
     COLUMNAS
     ========================================================= */
  const columnasCierresCuentas = [
    {
      key: "secuenciaReclamacion",
      title: "No. Reclamo",
      dataIndex: "secuenciaReclamacion",
    },
    {
      key: "nombreAfiliado",
      title: "Nombre Asegurado",
      dataIndex: "nombreAfiliado",
    },
    {
      key: "numeroPlastico",
      title: "No. Carnet",
      dataIndex: "numeroPlastico",
    },
    {
      key: "cedula",
      title: "Cédula",
      dataIndex: "cedula",
    },
    {
      key: "descripcionPlan",
      title: "Plan",
      dataIndex: "descripcionPlan",
    },
    {
      key: "fechaIngreso",
      title: "Fecha Servicio",
      dataIndex: "fechaIngreso",
      render: (_, record) =>
        moment(record.fechaIngreso).format(DATE_FORMATS.middledash_dmy),
    },
    {
      key: "fechaSalida",
      title: "Fecha Salida",
      dataIndex: "fechaSalida",
      render: (_, record) =>
        moment(record.fechaSalida).format(DATE_FORMATS.middledash_dmy),
    },
    {
      key: "action",
      title: "Acción",
      render: (_, record) => (
        <Space size={10}>
          <Tooltip text="Ver Detalle">
            <EyeOutlined
              style={{ color: empresa?.COLOR_PRINCIPAL, fontSize: 16 }}
              onClick={() => onPressVerDetalle(record)}
            />
          </Tooltip>
          <Tooltip text="Voucher">
            <DownloadOutlined
              style={{ color: empresa?.COLOR_PRINCIPAL, fontSize: 16 }}
              onClick={() => onPressVoucher(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const onPressVerDetalle = (record) => {
    // Construir el número de ingreso completo: año-secuencial
    const numeroIngresoCompleto = `${record.ano}-${record.secuencial}`;
    
    // Navegar a la pantalla de cierre de cuenta con el query param
    history.push(`/cierre-cuenta-clinica?numeroIngreso=${numeroIngresoCompleto}`);
  };

  const onPressVoucher = (record) => {
    setVoucherRecord(record);
    setEsMatricial(false);
    setShowVoucherModal(true);
  };

  // Función para cambiar de página
  const handleCambiarPagina = (nuevaPagina) => {
    const { 
      fechaInicio, 
      fechaFin,
      numeroReclamo,
      numeroCarnet,
      servicio,
      fechaServicioDesde,
      fechaServicioHasta,
      cedula,
      plan,
      medicoSeleccionado,
    } = formConsultaCierresCuentasData;

    // Preparar filtros: solo enviar si tienen valor
    const filtros = {
      numeroReclamo: numeroReclamo?.trim() || undefined,
      numeroCarnet: numeroCarnet?.trim() || undefined,
      servicio: servicio?.trim() || undefined,
      fechaServicioDesde: fechaServicioDesde?.trim() || undefined,
      fechaServicioHasta: fechaServicioHasta?.trim() || undefined,
      cedula: cedula?.trim() || undefined,
      plan: plan?.trim() || undefined,
    };

    const pssHonorario = medicoSeleccionado?.codigo
      ? Number(String(medicoSeleccionado.codigo).trim())
      : undefined;

    props.consultarCierresCuentas({
      formConsultaCierresCuentasData: formConsultaCierresCuentasData,
      formHandler: props.formConsultaCierresCuentas,
      reclamante: authUserData?.prestador?.codigo,
      fechaInicio,
      fechaFin,
      numeroReclamo: filtros.numeroReclamo,
      numeroCarnet: filtros.numeroCarnet,
      servicio: filtros.servicio,
      fechaServicioDesde: filtros.fechaServicioDesde,
      fechaServicioHasta: filtros.fechaServicioHasta,
      cedula: filtros.cedula,
      plan: filtros.plan,
      pssHonorario,
      numeroPagina: nuevaPagina,
      tamanoPagina: 10,
    });
  };

  const generarVoucher = async () => {
    if (!voucherRecord) return;

    setDescargandoVoucher(true);

    try {
      // Extraer ramo de la poliza (segundo elemento)
      const poliza = voucherRecord.poliza || "";
      const polizaParts = poliza.split("-");
      const ramo = polizaParts.length >= 2 ? Number(polizaParts[1]) : 95;

      // Obtener datos del PSS logueado
      const codigoPss = authUserData?.prestador?.codigo || 0;
      const tipoReclamante = voucherRecord.tipoReclamante || "NO_MEDICO";

      const payload = {
        Ano: Number(voucherRecord.ano),
        Compania: 30,
        Ramo: ramo,
        Secuencial: Number(voucherRecord.secuencial),
        CodigoPss: Number(codigoPss),
        TipoReclamante: tipoReclamante,
        EsMatricial: esMatricial,
      };

      const EgresosService = require("../../../services/egresos/EgresosService").default;
      await EgresosService.descargarVoucherEgreso(payload);

      setShowVoucherModal(false);
      setAlertConfig({
        type: "success",
        title: "Éxito",
        message: "Voucher descargado exitosamente.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
    } catch (error) {
      console.error("Error al descargar voucher:", error);
      setAlertConfig({
        type: "error",
        title: "Error",
        message: error?.mensaje || "Error al descargar el voucher.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
    } finally {
      setDescargandoVoucher(false);
    }
  };

  return (
    <>
      {listaOrigen.length > 0 && dataFiltrada.length === 0 ? (
        <CompactSection>
          <div style={{ 
            textAlign: "center", 
            padding: "20px 10px", 
            color: "#666",
            fontSize: "14px" 
          }}>
            No se encontraron registros que coincidan con los filtros aplicados (Nombre Asegurado, Cédula o Plan).
          </div>
        </CompactSection>
      ) : (
        <>
          <CompactSection>
            <CustomTable
              columns={columnasCierresCuentas}
              dataSource={dataFiltrada}
              showPagination={false}
              width={"200%"}
            />
          </CompactSection>
          
          {/* Paginación manual del backend - FUERA del Section de la tabla */}
          {cantidadTotal > 0 && (
            <CompactSection>
              <div style={{ 
                display: "flex", 
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                padding: "10px 0",
                width: "100%"
              }}>
                <div style={{ fontSize: "13px", color: "#666" }}>
                  Mostrando {dataFiltrada.length} de {listaOrigen.length} registros en página {paginaActual} | Total: {cantidadTotal}
                </div>
                
                <div style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "center" }}>
                  <Button
                    onClick={() => handleCambiarPagina(1)}
                    disabled={paginaActual === 1}
                  >
                    Primera
                  </Button>
                  <Button
                    onClick={() => handleCambiarPagina(paginaActual - 1)}
                    disabled={paginaActual === 1}
                  >
                    Anterior
                  </Button>
                  <span style={{ 
                    padding: "6px 12px", 
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    color: "#666",
                    fontWeight: "500"
                  }}>
                    Página {paginaActual} de {totalPaginas}
                  </span>
                  <Button
                    onClick={() => handleCambiarPagina(paginaActual + 1)}
                    disabled={paginaActual >= totalPaginas}
                  >
                    Siguiente
                  </Button>
                  <Button
                    onClick={() => handleCambiarPagina(totalPaginas)}
                    disabled={paginaActual >= totalPaginas}
                  >
                    Última
                  </Button>
                </div>
              </div>
            </CompactSection>
          )}
        </>
      )}

      {/* Modal de selección de tipo de voucher */}
      <CustomModal
        isOpen={showVoucherModal}
        onClose={() => setShowVoucherModal(false)}
        width="400px"
        maxWidth="90vw"
      >
        <div style={{ padding: "20px" }}>
          <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: "600" }}>
            Generar Voucher
          </h3>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "12px", fontWeight: "600", fontSize: "14px" }}>
              Seleccione el tipo de voucher:
            </label>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                cursor: "pointer", 
                padding: "10px", 
                border: `2px solid ${!esMatricial ? '#1890ff' : '#d9d9d9'}`, 
                borderRadius: "4px", 
                backgroundColor: !esMatricial ? '#e6f7ff' : 'transparent' 
              }}>
                <input
                  type="radio"
                  name="tipoVoucher"
                  checked={!esMatricial}
                  onChange={() => setEsMatricial(false)}
                  style={{ marginRight: "10px" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "500" }}>Voucher Térmico</span>
              </label>

              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                cursor: "pointer", 
                padding: "10px", 
                border: `2px solid ${esMatricial ? '#1890ff' : '#d9d9d9'}`, 
                borderRadius: "4px", 
                backgroundColor: esMatricial ? '#e6f7ff' : 'transparent' 
              }}>
                <input
                  type="radio"
                  name="tipoVoucher"
                  checked={esMatricial}
                  onChange={() => setEsMatricial(true)}
                  style={{ marginRight: "10px" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "500" }}>Voucher Matricial</span>
              </label>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "24px" }}>
            <Button
              onClick={() => setShowVoucherModal(false)}
              disabled={descargandoVoucher}
            >
              Cancelar
            </Button>
            <PrimaryButton
              onClick={generarVoucher}
              disabled={descargandoVoucher}
            >
              {descargandoVoucher ? (
                <>
                  <LoadingOutlined style={{ marginRight: "8px" }} spin />
                  Generando...
                </>
              ) : (
                'Generar'
              )}
            </PrimaryButton>
          </div>
        </div>
      </CustomModal>

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} />
      </CheckRender>
    </>
  );
};

const mapStateToProps = (state) => ({
  formConsultaCierresCuentasData:
    state.egreso.formConsultaCierresCuentasData,
  authUserData: state.auth.authUserData,
});

const mapDispatchToProps = {
  formConsultaCierresCuentas: EgresosActions.formConsultaCierresCuentas,
  consultarCierresCuentas: EgresosActions.consultarCierresCuentas,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BusquedaCierresCuentasTabla);
